import { useEffect, useState } from 'react'
import { Building2, Calendar, Car, Tags, Gauge, FileText, FileSpreadsheet, Filter } from 'lucide-react'
import api from '../services/api'
import LookupSelect from '../components/LookupSelect'
import { useEmpresa } from '../contexts/EmpresaContext'
import { Linha } from './LancamentoDepositos'

interface Veiculo {
  veicod: string; veipla: string; tipo: string; empresa: number
  prev: number | null; medias: Record<string, number>; acum: number | null; abaixo: number
}
interface Resultado { dias: string[]; veiculos: Veiculo[] }
interface Opcao { cod: string; des: string }

const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab']
const fmt = (v: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const ACIMA_FATOR = 1.3 // verde quando a média passa 30% da prevista

type Destaque = '' | 'vermelho' | 'verde' | 'ambos'
const DESTAQUES: { id: Destaque; rotulo: string; dica: string; cor?: string; borda?: string; fundoAtivo: string; corAtiva: string }[] = [
  { id: '', rotulo: 'Todos', dica: 'Todos os veículos', fundoAtivo: '#1a6ea8', corAtiva: '#fff' },
  { id: 'vermelho', rotulo: 'Só vermelhos', dica: 'Algum dia abaixo da média prevista', cor: '#fee2e2', borda: '#fca5a5', fundoAtivo: '#fee2e2', corAtiva: '#991b1b' },
  { id: 'verde', rotulo: 'Só verdes', dica: 'Algum dia mais de 30% acima da prevista', cor: '#dcfce7', borda: '#86efac', fundoAtivo: '#dcfce7', corAtiva: '#166534' },
  { id: 'ambos', rotulo: 'Vermelhos e verdes', dica: 'Algum dia abaixo ou mais de 30% acima da prevista', fundoAtivo: '#1a6ea8', corAtiva: '#fff' },
]
const hojeIso = () => new Date().toISOString().slice(0, 10)
const inicioMes = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10) }
const semana = (d: string) => DIAS_SEMANA[new Date(`${d}T12:00:00Z`).getUTCDay()]

// Estatística de médias de combustível (GeneXus WMTZ001 → WDPS098), calculada na hora a partir dos abastecimentos.
export default function RelatorioMediaCombustivel() {
  const { empresas } = useEmpresa()
  const [empresa, setEmpresa] = useState('0')
  const [tipo, setTipo] = useState('')
  const [veiculo, setVeiculo] = useState('')
  const [dataIni, setDataIni] = useState(inicioMes())
  const [dataFim, setDataFim] = useState(hojeIso())
  const [somenteComMedia, setSomenteComMedia] = useState(true)
  const [destaque, setDestaque] = useState<Destaque>('')
  const [tipos, setTipos] = useState<Opcao[]>([])
  const [res, setRes] = useState<Resultado | null>(null)
  const [carregando, setCarregando] = useState(false)
  const [gerando, setGerando] = useState<'' | 'pdf' | 'excel'>('')
  const [erro, setErro] = useState('')

  useEffect(() => { api.get('/combustivel/tipos-veiculo').then(r => setTipos(r.data)).catch(() => {}) }, [])

  const params = (d: Destaque = destaque) => ({ empresa, tipo, veiculo, dataIni, dataFim, somenteComMedia: somenteComMedia ? '1' : '0', destaque: d })

  async function gerar(d: Destaque = destaque) {
    setErro(''); setCarregando(true)
    try { setRes((await api.get('/combustivel/relatorios/media-combustivel', { params: params(d) })).data) }
    catch (e: any) { setRes(null); setErro(e?.response?.data?.error || 'Erro ao gerar relatório.') }
    finally { setCarregando(false) }
  }

  async function exportar(formato: 'pdf' | 'excel') {
    setErro(''); setGerando(formato)
    try {
      const r = await api.get(`/combustivel/relatorios/media-combustivel/${formato}`, { params: params(), responseType: 'blob' })
      const tipoArq = formato === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      const url = URL.createObjectURL(new Blob([r.data], { type: tipoArq }))
      if (formato === 'pdf') window.open(url, '_blank')
      else { const a = document.createElement('a'); a.href = url; a.download = 'medias-combustivel.xlsx'; a.click(); URL.revokeObjectURL(url) }
    } catch (e: any) {
      // Erro de validação vem como blob JSON.
      try { const t = await e?.response?.data?.text?.(); setErro(JSON.parse(t).error) } catch { setErro(formato === 'pdf' ? 'Erro ao gerar PDF.' : 'Erro ao gerar Excel.') }
    } finally { setGerando('') }
  }

  const th: React.CSSProperties = { padding: '0.4rem 0.3rem', fontSize: '0.64rem', fontWeight: 800, color: '#fff', background: '#1a6ea8', whiteSpace: 'nowrap', textAlign: 'center', position: 'sticky', top: 0, zIndex: 1 }
  const td: React.CSSProperties = { padding: '0.3rem 0.35rem', fontSize: '0.74rem', borderBottom: '1px solid #eef2f7', whiteSpace: 'nowrap', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }
  // Colunas fixas à esquerda ao rolar pro lado.
  const fixa = (left: number, extra?: React.CSSProperties): React.CSSProperties => ({ position: 'sticky', left, zIndex: 2, ...extra })
  const totalAbaixo = res ? res.veiculos.filter(v => v.acum != null && v.prev != null && v.acum < v.prev).length : 0
  // Muito acima (> 30% da prevista): hodômetro errado ou abastecimento anterior faltando
  const muitoAcima = (m: number | null | undefined, prev: number | null) => m != null && prev != null && m > prev * ACIMA_FATOR
  const totalAcima = res ? res.veiculos.filter(v => muitoAcima(v.acum, v.prev)).length : 0

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
      <div className="modal-box" style={{ width: '100%', maxWidth: 680, maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1a6ea8, #14547f)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 9, background: 'rgba(255,255,255,0.15)' }}>
              <Gauge size={18} color="#fff" />
            </span>
            <div>
              <h2>Média de Combustível</h2>
              <div style={{ fontSize: '0.7rem', color: '#dbeafe', marginTop: 2 }}>Média km/l de cada abastecimento, por veículo e dia</div>
            </div>
          </div>
        </div>

        <div className="modal-corpo">
          <Linha label="Empresa" icon={Building2}>
            <select value={empresa} onChange={e => setEmpresa(e.target.value)} style={{ maxWidth: 280 }}>
              <option value="0">Todas</option>
              {empresas.map(e => <option key={e.empcod} value={e.empcod}>{e.empcod} — {e.empfan?.trim() || e.empnom?.trim()}</option>)}
            </select>
          </Linha>
          <Linha label="Tipo" icon={Tags}>
            <select value={tipo} onChange={e => setTipo(e.target.value)} style={{ maxWidth: 280 }}>
              <option value="">Todos</option>
              {tipos.map(t => <option key={t.cod} value={t.cod}>{t.des}</option>)}
            </select>
          </Linha>
          <Linha label="Veículo" icon={Car}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <div style={{ maxWidth: 140 }}>
                <LookupSelect endpoint="/erp/veiculos" codKey="veicod" descKey="veipla" tagKey="veicod"
                  codValue={veiculo} descValue={veiculo} onSelect={cod => setVeiculo(cod)} placeholder="Todos" />
              </div>
              {veiculo
                ? <button className="btn btn-secondary" style={{ padding: '0.25rem 0.6rem', fontSize: '0.72rem' }} onClick={() => setVeiculo('')}>Todos</button>
                : <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>em branco = todos</span>}
            </div>
          </Linha>
          <Linha label="Período" icon={Calendar}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <input type="date" value={dataIni} onChange={e => setDataIni(e.target.value)} style={{ maxWidth: 170 }} />
              <span style={{ fontSize: '0.8rem', color: '#57606f' }}>a</span>
              <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} style={{ maxWidth: 170 }} />
            </div>
            <div style={{ fontSize: '0.72rem', marginTop: 4, color: '#94a3b8' }}>Até 62 dias (uma coluna por dia).</div>
          </Linha>
          <Linha label="Exibir" icon={Gauge}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.82rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={somenteComMedia} onChange={e => setSomenteComMedia(e.target.checked)} /> Só veículos com média no período
            </label>
          </Linha>
          <Linha label="Destaque" icon={Filter}>
            <div style={{ display: 'inline-flex', border: '1px solid #cbd5e1', borderRadius: 8, overflow: 'hidden' }}>
              {DESTAQUES.map((o, i) => {
                const ativo = destaque === o.id
                return (
                  <button key={o.id} type="button" onClick={() => { setDestaque(o.id); if (res) gerar(o.id) }}
                    title={o.dica}
                    style={{
                      border: 'none', borderLeft: i ? '1px solid #cbd5e1' : 'none', padding: '0.35rem 0.75rem', fontSize: '0.78rem', cursor: 'pointer',
                      display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: ativo ? 700 : 500,
                      background: ativo ? o.fundoAtivo : '#fff', color: ativo ? o.corAtiva : '#475569',
                    }}>
                    {o.cor && <span style={{ width: 10, height: 10, borderRadius: 2, background: o.cor, border: `1px solid ${o.borda}` }} />}
                    {o.rotulo}
                  </button>
                )
              })}
            </div>
            <div style={{ fontSize: '0.72rem', marginTop: 4, color: '#94a3b8' }}>Mostra só os veículos com algum dia na cor escolhida (vale também pro PDF e Excel).</div>
          </Linha>
          {erro && (
            <div style={{ marginTop: '0.9rem', fontSize: '0.8rem', fontWeight: 600, padding: '0.55rem 0.9rem', borderRadius: 8, background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}>{erro}</div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-primary" onClick={() => gerar()} disabled={carregando}>{carregando ? 'Gerando...' : 'Gerar'}</button>
          <button className="btn" style={{ background: '#dc2626', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }} onClick={() => exportar('pdf')} disabled={!!gerando}>
            <FileText size={15} /> {gerando === 'pdf' ? 'Gerando PDF...' : 'PDF'}
          </button>
          <button className="btn" style={{ background: '#15803d', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }} onClick={() => exportar('excel')} disabled={!!gerando}>
            <FileSpreadsheet size={15} /> {gerando === 'excel' ? 'Gerando Excel...' : 'Excel'}
          </button>
        </div>
      </div>

      {res && (
        <div className="modal-box" style={{ width: '100%', maxWidth: 'none', maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
          <div className="modal-corpo">
            <div style={{ display: 'flex', gap: '1.2rem', flexWrap: 'wrap', alignItems: 'center', fontSize: '0.8rem', color: '#475569', marginBottom: '0.7rem' }}>
              <span><b>{res.veiculos.length}</b> veículo(s)</span>
              <span><b style={{ color: '#b91c1c' }}>{totalAbaixo}</b> com média acumulada abaixo da prevista</span>
              <span><b style={{ color: '#15803d' }}>{totalAcima}</b> com média acumulada muito acima</span>
            </div>
            <div style={{ display: 'flex', gap: '1.4rem', flexWrap: 'wrap', fontSize: '0.75rem', color: '#475569', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.5rem 0.8rem', marginBottom: '0.7rem' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 12, height: 12, borderRadius: 2, background: '#fee2e2', border: '1px solid #fecaca' }} />
                <span><b style={{ color: '#991b1b' }}>Vermelho:</b> média abaixo da prevista (Prev) — consumo acima do esperado.</span>
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 12, height: 12, borderRadius: 2, background: '#dcfce7', border: '1px solid #bbf7d0' }} />
                <span><b style={{ color: '#166534' }}>Verde:</b> média mais de 30% acima da prevista (ex.: Prev 5,00 → acima de 6,50) — confira o hodômetro ou se falta algum abastecimento anterior.</span>
              </span>
            </div>
            <div style={{ overflow: 'auto', maxHeight: '70vh', border: '1px solid #bfdbfe', borderRadius: 8 }}>
              <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ ...th, ...fixa(0, { zIndex: 3 }), minWidth: 30 }}>Emp</th>
                    <th style={{ ...th, ...fixa(34, { zIndex: 3 }), minWidth: 52 }}>Veículo</th>
                    <th style={{ ...th, ...fixa(90, { zIndex: 3 }), minWidth: 70 }}>Placa</th>
                    <th style={{ ...th, ...fixa(164, { zIndex: 3 }), minWidth: 34 }}>Tip</th>
                    {res.dias.map(d => (
                      <th key={d} style={{ ...th, minWidth: 44, background: ['Sab', 'Dom'].includes(semana(d)) ? '#155a8a' : '#1a6ea8' }}>
                        {d.slice(8, 10)}<br /><span style={{ fontWeight: 600, opacity: 0.85 }}>{semana(d)}</span>
                      </th>
                    ))}
                    <th style={{ ...th, minWidth: 50, background: '#0f2a52' }}>Acum</th>
                    <th style={{ ...th, minWidth: 46, background: '#475569' }}>Prev</th>
                  </tr>
                </thead>
                <tbody>
                  {res.veiculos.map((v, i) => {
                    const fundo = i % 2 ? '#f5f9fd' : '#fff'
                    return (
                      <tr key={v.veicod}>
                        <td style={{ ...td, ...fixa(0), background: fundo, textAlign: 'center', color: '#94a3b8' }}>{v.empresa}</td>
                        <td style={{ ...td, ...fixa(34), background: fundo, textAlign: 'left', fontWeight: 700, color: '#1a6ea8' }}>{v.veicod}</td>
                        <td style={{ ...td, ...fixa(90), background: fundo, textAlign: 'left', color: '#b91c1c' }}>{v.veipla}</td>
                        <td style={{ ...td, ...fixa(164), background: fundo, textAlign: 'left', color: '#475569' }}>{v.tipo}</td>
                        {res.dias.map(d => {
                          const m = v.medias[d]
                          const baixa = m != null && v.prev != null && m < v.prev
                          const alta = muitoAcima(m, v.prev)
                          return (
                            <td key={d} title={m != null ? `${v.veicod} em ${d.split('-').reverse().join('/')}: ${fmt(m)} km/l${v.prev ? ` (prevista ${fmt(v.prev)})` : ''}${alta ? ' — mais de 30% acima da prevista' : ''}` : undefined}
                              style={{ ...td, background: baixa ? '#fee2e2' : alta ? '#dcfce7' : fundo, color: baixa ? '#991b1b' : alta ? '#166534' : '#111827' }}>
                              {m != null ? fmt(m) : ''}
                            </td>
                          )
                        })}
                        <td style={{ ...td, background: fundo, fontWeight: 800, color: v.acum != null && v.prev != null && v.acum < v.prev ? '#b91c1c' : muitoAcima(v.acum, v.prev) ? '#15803d' : '#1b3f7b' }}>{v.acum != null ? fmt(v.acum) : ''}</td>
                        <td style={{ ...td, background: fundo, color: '#475569' }}>{v.prev != null ? fmt(v.prev) : ''}</td>
                      </tr>
                    )
                  })}
                  {res.veiculos.length === 0 && <tr><td colSpan={res.dias.length + 6} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: '1.2rem' }}>Nenhum veículo com média no período.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
